import React, { useEffect, useCallback, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";

import Button from "components/atoms/Button";

import dealerCareApi from "services/dealerCareApi";

import { Terms, useAuth } from "context/AuthContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "70vh",
  bgcolor: "background.paper",
  borderRadius: 1,
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  p: 3,
  width: 500,
};

const contentStyle = {
  flexGrow: 1,
  overflowY: "scroll",
  bgcolor: "#f5f5f5",
  borderRadius: 1,
  p: 2,
  mb: 2,
};

interface TermsModalProps {
  visible: boolean;
  showActions: boolean;
  canClose: boolean;
  alreadyAccepted: boolean;
  onClose: () => void;
}

const TermsModal = ({
  canClose,
  showActions,
  onClose,
  visible,
  alreadyAccepted,
}: TermsModalProps) => {
  const [checked, setChecked] = useState(false);
  const [termsText, setTermsText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingAccept, setIsLoadingAccept] = useState(false);

  const { profileData, updateTerms } = useAuth();

  const handleCheckboxChange = (event: any) => {
    setChecked(event.target.checked);
  };

  const handleRememberLater = () => {
    localStorage.setItem(
      "@ctech:shownTermsDate",
      new Date().valueOf().toString(),
    );
    onClose();
  };

  const handleGetDealerInfo = useCallback(async (): Promise<Terms> => {
    const { data } = await dealerCareApi.get(
      `v1/dealers/${profileData.dealerId}`,
    );

    updateTerms(data.data.termsOfService);

    return data.data.termsOfService;
  }, [profileData.dealerId, updateTerms]);

  const handleGetTerms = useCallback(async () => {
    try {
      setIsLoading(true);
      const dealerInfoData = await handleGetDealerInfo();

      const { data } = await dealerCareApi.get(
        `v1/agreements/${dealerInfoData?.agreement.id}`,
      );

      setTermsText(data.data.content);
    } catch {
      toast("Falha ao carregar termos!", {
        type: "error",
        autoClose: 500,
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  }, [onClose, handleGetDealerInfo]);

  const handleAcceptTerms = useCallback(async () => {
    try {
      setIsLoadingAccept(true);

      await dealerCareApi.post(`v1/dealers/${profileData.dealerId}/accept`, {
        type: "termsOfService",
      });

      const dealerData = await dealerCareApi.get(
        `v1/dealers/${profileData.dealerId}`,
      );

      updateTerms(dealerData.data.data.termsOfService);
      onClose();
    } catch {
      toast("Falha ao aceitar termos!", {
        type: "error",
        autoClose: 500,
      });
    } finally {
      setIsLoadingAccept(false);
    }
  }, [updateTerms, onClose, profileData.dealerId]);

  useEffect(() => {
    if (visible) handleGetTerms();
  }, [handleGetTerms, visible]);

  return (
    <Modal
      open={visible}
      onClose={canClose || alreadyAccepted ? onClose : undefined}
    >
      <Box sx={modalStyle}>
        <Typography variant="h6" component="h2" gutterBottom>
          Conheça nossos Termos e Condições
        </Typography>
        {isLoading ? (
          <Box
            sx={{
              alignItems: "center",
              justifyContent: "center",
              p: 5,
              display: "flex",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={contentStyle}>
              <Typography variant="body2">
                <ReactMarkdown>{termsText}</ReactMarkdown>
              </Typography>
            </Box>
            {showActions && (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={handleCheckboxChange}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Li e estou de acordo com os Termos e Condições do Portal
                    </Typography>
                  }
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                  }}
                >
                  {canClose && (
                    <Button
                      variant="outlined"
                      sx={{ mr: 2 }}
                      onClick={handleRememberLater}
                    >
                      LEMBRE-ME DEPOIS
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAcceptTerms}
                    disabled={!checked || isLoadingAccept}
                    isLoading={isLoadingAccept}
                  >
                    CONTINUAR
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default TermsModal;
